// Custom Image extension for the agreement-template editor.
//
// Built on top of @tiptap/extension-image, with two additions:
//
//   1. ATTRIBUTES:
//      • float — 'left' | 'right' | 'center' | 'none'  (default 'none')
//      • width — pixel/percent value applied to the rendered <img> (e.g.
//        '240px', '50%'). Drives both display sizing and the saved HTML.
//
//      Both render as inline CSS so the saved html_content carries layout
//      intent; the public sign page and PDF render path consume the same
//      content and inherit the layout automatically — no separate stylesheet.
//
//   2. RESIZE NODE-VIEW:
//      A custom NodeView wraps each image in a <span> and overlays four
//      corner drag handles. Hover or click an image and the handles
//      appear; drag to resize. Aspect ratio is preserved (height auto).
//      The new width is committed back to the node's `width` attribute on
//      mouseup, so a resize survives save/reload and round-trips through
//      the BE unchanged.
//
// All implementation is plain DOM — no extra deps, no React-in-NodeView
// complexity. The NodeView's `update()` re-applies attribute changes
// (so toolbar buttons that change `float` or `width` re-render the image
// without a full editor reset).

import Image from '@tiptap/extension-image';

const FLOAT_VALUES = new Set(['left', 'right', 'center', 'none']);

// Build the wrapper's CSS for the float attribute. The wrapper carries
// the float — the inner <img> never floats directly, otherwise the
// resize handles wouldn't share the floated bounding box.
//
// For center: wrapper is full-width block + text-align:center; the
// IMAGE inside also gets display:block + margin:0 auto so it actually
// centers within the wrapper. Setting text-align alone isn't enough
// when the image is rendered as a block (which our extension forces
// via group: 'block'); a block-level child ignores the parent's
// text-align.
function wrapperStyleForFloat(float) {
  switch (float) {
    case 'left':
      return 'float:left;margin:0 16px 8px 0;';
    case 'right':
      return 'float:right;margin:0 0 8px 16px;';
    case 'center':
      return 'display:block;width:100%;float:none;clear:both;text-align:center;margin:8px 0;';
    case 'none':
    default:
      return 'display:inline-block;';
  }
}

// Image-level style overrides for float modes that the wrapper alone
// can't satisfy. Currently only center needs this — `display:block` +
// `margin:0 auto` actually centers the image within the wrapper.
function imageStyleForFloat(float) {
  if (float === 'center') {
    return 'display:block;margin:0 auto;';
  }
  return '';
}

// Single-handle resize controller. We expose ALL four corners as handles
// (top-left, top-right, bottom-left, bottom-right) so you can resize from
// any direction. Aspect ratio is preserved by using width only and letting
// height: auto on the image.
const HANDLE_POSITIONS = [
  { name: 'tl', cursor: 'nwse-resize', top: -6, left: -6 },
  { name: 'tr', cursor: 'nesw-resize', top: -6, right: -6 },
  { name: 'bl', cursor: 'nesw-resize', bottom: -6, left: -6 },
  { name: 'br', cursor: 'nwse-resize', bottom: -6, right: -6 },
];

const HANDLE_CSS = `
  position:absolute;
  width:12px;
  height:12px;
  background:#115e59;
  border:2px solid #ffffff;
  border-radius:50%;
  box-shadow:0 0 0 1px rgba(0,0,0,0.15);
  z-index:2;
  /* default: hidden — wrapper-level :hover and the .selected class flip
     this to visible. Inline styles + a stylesheet rule both apply, but
     :hover requires a real CSS rule, so we inject one once below. */
  display:none;
`;

// Inject :hover + .selected CSS for the handles + wrapper outline once.
// Idempotent — if multiple editors mount on the same page we only insert
// the rule the first time.
function ensureResizeStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('tiptap-image-resize-styles')) return;
  const style = document.createElement('style');
  style.id = 'tiptap-image-resize-styles';
  style.textContent = `
    .tiptap-image-wrapper { position:relative; }
    .tiptap-image-wrapper:hover .tiptap-image-handle,
    .tiptap-image-wrapper.is-selected .tiptap-image-handle {
      display:block !important;
    }
    .tiptap-image-wrapper:hover,
    .tiptap-image-wrapper.is-selected {
      outline:1px dashed #115e59;
      outline-offset:2px;
    }
    .tiptap-image-wrapper img { vertical-align:middle; max-width:100%; height:auto; }
  `;
  document.head.appendChild(style);
}

const CustomImage = Image.extend({
  // Block-level (not inline) so float/center work cleanly. Inline images
  // would mix with surrounding text and the resize wrapper would behave
  // oddly.
  inline: false,
  group: 'block',

  addAttributes() {
    return {
      ...this.parent?.(),
      float: {
        default: 'none',
        parseHTML: (el) => {
          const v = el.getAttribute('data-float') || 'none';
          return FLOAT_VALUES.has(v) ? v : 'none';
        },
        renderHTML: (attrs) => (attrs.float && attrs.float !== 'none' ? { 'data-float': attrs.float } : {}),
      },
      width: {
        default: null,
        parseHTML: (el) => {
          const raw =
            el.getAttribute('data-width') ||
            el.style?.width ||
            el.getAttribute('width') ||
            null;
          if (!raw) return null;
          // DOCX-imported images come through with a bare numeric `width`
          // attribute (no unit). Normalize so the CSS we emit later is
          // valid (`width:640px;` not `width:640;`).
          return /^\d+(\.\d+)?$/.test(String(raw).trim()) ? `${String(raw).trim()}px` : raw;
        },
        renderHTML: (attrs) => (attrs.width ? { 'data-width': attrs.width } : {}),
      },
    };
  },

  // renderHTML controls how the image is serialised into the saved HTML.
  // We emit a single <img> with inline style for float/width — the same
  // markup renders correctly outside the editor (sign page, PDF, email).
  //
  // For float left/right we use float CSS directly on the <img>. For
  // center we use display:block + margin:0 auto. For none we let the
  // image flow inline like a normal inline-block image.
  //
  // Note: the per-attribute `renderHTML` above renames `float` → `data-float`
  // and `width` → `data-width` in HTMLAttributes, so destructuring `float` /
  // `width` here returns undefined. Read the `data-*` keys instead — they
  // hold the actual values produced by the attribute renderers.
  renderHTML({ HTMLAttributes }) {
    const {
      'data-float': dataFloat,
      'data-width': dataWidth,
      float: _f,
      width: _w,
      ...rest
    } = HTMLAttributes;
    const float = dataFloat || 'none';
    const width = dataWidth || null;
    const f = float && FLOAT_VALUES.has(float) ? float : 'none';
    let layoutCSS;
    if (f === 'left') layoutCSS = 'float:left;margin:0 16px 8px 0;';
    else if (f === 'right') layoutCSS = 'float:right;margin:0 0 8px 16px;';
    else if (f === 'center') layoutCSS = 'display:block;margin:8px auto;';
    else layoutCSS = '';

    const styleParts = [
      layoutCSS,
      width ? `width:${width};` : 'max-width:100%;',
      'height:auto;',
    ];
    return [
      'img',
      {
        ...rest,
        ...(f !== 'none' ? { 'data-float': f } : {}),
        ...(width ? { 'data-width': width } : {}),
        style: styleParts.filter(Boolean).join(''),
      },
    ];
  },

  // NodeView — what the editor RENDERS while editing. Wraps the image in
  // a <span> with corner drag handles. Doesn't affect saved HTML; that
  // comes from renderHTML above.
  addNodeView() {
    return ({ node, getPos, editor }) => {
      ensureResizeStyles();

      const wrapper = document.createElement('span');
      wrapper.className = 'tiptap-image-wrapper';

      const img = document.createElement('img');
      img.src = node.attrs.src || '';
      if (node.attrs.alt) img.alt = node.attrs.alt;
      if (node.attrs.title) img.title = node.attrs.title;

      // Apply attribute-driven styles to the wrapper / img. Width is
      // applied to the image itself (drag-resize updates this directly).
      // Float is applied to the wrapper EXCEPT for center, where the
      // image also needs display:block + margin:auto to actually center.
      const applyAttrStyles = (n) => {
        const f = n.attrs.float || 'none';
        wrapper.style.cssText = wrapperStyleForFloat(f);
        // Reset image-level layout overrides each apply, then re-add
        // whatever the current float demands. Avoids stale display/margin
        // bleeding across float changes.
        img.style.removeProperty('display');
        img.style.removeProperty('margin');
        const imgLayout = imageStyleForFloat(f);
        if (imgLayout) {
          imgLayout
            .split(';')
            .filter(Boolean)
            .forEach((rule) => {
              const [k, v] = rule.split(':').map((s) => s.trim());
              if (k && v) img.style.setProperty(k, v);
            });
        }
        if (n.attrs.width) img.style.width = n.attrs.width;
        else img.style.removeProperty('width');
      };
      applyAttrStyles(node);
      wrapper.appendChild(img);

      // Build + attach the four corner handles. Each handle has a unique
      // direction so the cursor and the dx/dy interpretation differ.
      const handles = HANDLE_POSITIONS.map((p) => {
        const h = document.createElement('span');
        h.className = 'tiptap-image-handle';
        h.dataset.corner = p.name;
        h.style.cssText = HANDLE_CSS + `cursor:${p.cursor};`;
        if (p.top != null) h.style.top = `${p.top}px`;
        if (p.bottom != null) h.style.bottom = `${p.bottom}px`;
        if (p.left != null) h.style.left = `${p.left}px`;
        if (p.right != null) h.style.right = `${p.right}px`;
        wrapper.appendChild(h);
        return h;
      });

      let dragging = false;
      let startX = 0;
      let startWidth = 0;
      let activeCorner = null;

      const onMouseMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        // Direction: TL/BL drag LEFT to grow; TR/BR drag RIGHT to grow.
        const grow = activeCorner === 'tr' || activeCorner === 'br' ? dx : -dx;
        const next = Math.max(40, Math.round(startWidth + grow));
        img.style.width = `${next}px`;
      };

      const onMouseUp = () => {
        if (!dragging) return;
        dragging = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        const finalWidth = img.style.width || null;
        // Persist via setNodeMarkup so undo / redo / save round-trip work.
        if (typeof getPos === 'function' && finalWidth) {
          editor.commands.command(({ tr }) => {
            tr.setNodeMarkup(getPos(), null, {
              ...node.attrs,
              width: finalWidth,
            });
            return true;
          });
        }
      };

      const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragging = true;
        activeCorner = e.currentTarget.dataset.corner;
        startX = e.clientX;
        startWidth = img.getBoundingClientRect().width;
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      handles.forEach((h) => h.addEventListener('mousedown', handleMouseDown));

      return {
        dom: wrapper,
        update(updatedNode) {
          if (updatedNode.type.name !== node.type.name) return false;
          img.src = updatedNode.attrs.src || '';
          img.alt = updatedNode.attrs.alt || '';
          img.title = updatedNode.attrs.title || '';
          applyAttrStyles(updatedNode);
          return true;
        },
        selectNode() {
          wrapper.classList.add('is-selected');
        },
        deselectNode() {
          wrapper.classList.remove('is-selected');
        },
        destroy() {
          handles.forEach((h) => h.removeEventListener('mousedown', handleMouseDown));
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
        },
      };
    };
  },
});

// Width presets exposed to the toolbar (small / medium / large / full).
// Free-form widths set via drag-resize still work; these are just quick
// presets the toolbar offers as buttons.
export const IMAGE_WIDTH_PRESETS = [
  { label: 'S', value: '120px', title: 'Small' },
  { label: 'M', value: '240px', title: 'Medium' },
  { label: 'L', value: '360px', title: 'Large' },
  { label: 'Full', value: '100%', title: 'Full width' },
];

export default CustomImage;
